/** One request on a page visit's timeline, in `performance.now()` ms. */
export type TimelineRecord = {
  id: string;
  label: string;
  dispatchedAt: number;
  /** Absent for a cache hit and while the request is still queued. */
  fetchStartedAt?: number;
  /** Absent while in flight. */
  finishedAt?: number;
  /** Earliest draw of a chart this record feeds, at or after `finishedAt`. */
  drawnAt?: number;
  fromCache: boolean;
};

export type TimelineSpan = { startedAt: number; endedAt: number };

type PhaseName = 'shell' | 'fetching' | 'drawing';

export type Phase = { name: PhaseName; ms: number; share: number };

/** Fractions of the span, so the drawer can lay the bar out in percentages. */
export type RecordBar = {
  id: string;
  left: number;
  queued: number;
  network: number;
  drawn: number;
  isPending: boolean;
  fromCache: boolean;
};

const clamp01 = (value: number) => Math.min(Math.max(value, 0), 1);
const atLeastZero = (value: number) => Math.max(value, 0);

const endOf = (record: TimelineRecord) =>
  record.drawnAt ?? record.finishedAt ?? record.dispatchedAt;

/**
 * The span a group of requests is drawn against when there is no page visit
 * to anchor them — a filter change, or records captured before the page
 * settled was measured. Runs from the first dispatch to the last end.
 */
export const spanOfRecords = (
  records: TimelineRecord[]
): TimelineSpan | null => {
  if (records.length === 0) return null;
  const startedAt = Math.min(...records.map((record) => record.dispatchedAt));
  const endedAt = Math.max(...records.map(endOf));
  return { startedAt, endedAt };
};

/**
 * Where a visit's time went, in three phases: before the first request was
 * dispatched (the page shell: config, flags, filter resolution), from then to
 * the last response, and from the last response to the last loader clearing.
 * Overlap is resolved in that order, so the three always sum to the total.
 */
export const buildPhases = (
  span: TimelineSpan,
  records: TimelineRecord[]
): Phase[] => {
  const total = atLeastZero(span.endedAt - span.startedAt);
  if (total === 0) return [];

  const dispatches = records.map((record) => record.dispatchedAt);
  const finishes = records.map((record) => record.finishedAt ?? span.endedAt);
  const firstDispatch =
    dispatches.length === 0 ? span.endedAt : Math.min(...dispatches);
  const lastFinish =
    finishes.length === 0 ? span.endedAt : Math.max(...finishes);

  const shellEnd = Math.min(
    Math.max(firstDispatch, span.startedAt),
    span.endedAt
  );
  const fetchEnd = Math.min(Math.max(lastFinish, shellEnd), span.endedAt);

  const ms: Record<PhaseName, number> = {
    shell: shellEnd - span.startedAt,
    fetching: fetchEnd - shellEnd,
    drawing: span.endedAt - fetchEnd,
  };

  return (['shell', 'fetching', 'drawing'] as const).map((name) => ({
    name,
    ms: ms[name],
    share: ms[name] / total,
  }));
};

/** Each record as a bar positioned on the span. */
export const buildRecordBars = (
  span: TimelineSpan,
  records: TimelineRecord[]
): RecordBar[] => {
  const length = span.endedAt - span.startedAt;
  const fraction = (ms: number) => (length <= 0 ? 0 : clamp01(ms / length));

  return records.map((record) => {
    const isPending = record.finishedAt === undefined;
    const fetchStart = record.fetchStartedAt ?? record.finishedAt;
    const finish = record.finishedAt ?? span.endedAt;
    const queuedEnd = fetchStart ?? finish;

    return {
      id: record.id,
      left: fraction(record.dispatchedAt - span.startedAt),
      queued: fraction(atLeastZero(queuedEnd - record.dispatchedAt)),
      network: fraction(atLeastZero(finish - queuedEnd)),
      drawn: fraction(atLeastZero((record.drawnAt ?? finish) - finish)),
      isPending,
      fromCache: record.fromCache,
    };
  });
};

/** The request the page spent longest waiting on, dispatch to response. */
export const longestRequest = (
  records: TimelineRecord[]
): { label: string; ms: number } | null => {
  let longest: { label: string; ms: number } | null = null;
  for (const record of records) {
    if (record.finishedAt === undefined) continue;
    const ms = record.finishedAt - record.dispatchedAt;
    if (longest === null || ms > longest.ms)
      longest = { label: record.label, ms };
  }
  return longest;
};
